import {Injectable} from '@angular/core';
import {PageEvent} from '@angular/material/paginator';
import {CompositeFilterDescriptor, SortDescriptor} from '@progress/kendo-data-query';
import {cloneDeep, forEach, isEqual} from 'lodash';
import {BehaviorSubject} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';

import {GridPagerService} from './grid-pager.service';
import {GridSelectorService} from './grid-selector.service';

@Injectable()
export class GridDataSourceService {
  private gridDataSubject$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public gridData$ = this.gridDataSubject$.asObservable();
  private totalItemsSubject$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public totalItems$ = this.totalItemsSubject$.asObservable();
  private needUpdateGridSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public needUpdateGrid$ = this.needUpdateGridSubject$.asObservable();
  private loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject$.asObservable();
  private upFetchingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public upFetching$ = this.upFetchingSubject$.asObservable();
  private sortSubject$: BehaviorSubject<SortDescriptor> = new BehaviorSubject<SortDescriptor>({
    dir: null,
    field: null
  });
  public sort$ = this.sortSubject$
    .asObservable()
    .pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)));
  private filtersSubject$: BehaviorSubject<CompositeFilterDescriptor> =
    new BehaviorSubject<CompositeFilterDescriptor>({
      logic: 'and',
      filters: []
    });
  public filters$ = this.filtersSubject$
    .asObservable()
    .pipe(distinctUntilChanged((prev, curr) => isEqual(prev.filters, curr.filters)));

  constructor(
    private gridSelectorService: GridSelectorService,
    private gridPagerService: GridPagerService
  ) {}

  public loadData(data: any[], total: number) {
    this.gridDataSubject$.next(data);
    this.totalItemsSubject$.next(total);
  }

  public set loading(value: boolean) {
    this.loadingSubject$.next(value);
  }

  public set upFetching(value: boolean) {
    this.upFetchingSubject$.next(value);
  }

  public getSort() {
    return this.sortSubject$.getValue();
  }

  public getFilters() {
    return this.filtersSubject$.getValue();
  }

  public getItems() {
    return this.gridDataSubject$.getValue();
  }

  public updateItem(item: any, fieldToSearch: string) {
    const updatedItem = cloneDeep(item);

    const updated = this.gridDataSubject$.getValue().map(gridItem => {
      if (gridItem[fieldToSearch] === item[fieldToSearch]) {
        gridItem = updatedItem;
      }

      return gridItem;
    });

    this.gridDataSubject$.next(updated);
    this.triggerUpdateGrid();
    this.triggerUpdateSelection(updatedItem, fieldToSearch);
  }

  public updateItems(items: any[], fieldToSearch: string) {
    const data = this.gridDataSubject$.getValue();
    const updatedItemsMap = new Map<string, any>();

    forEach(items, node => updatedItemsMap.set(node[fieldToSearch], cloneDeep(node)));

    const updatedItems = data
      .map((gridItem, index) => {
        const item = updatedItemsMap.has(gridItem[fieldToSearch])
          ? updatedItemsMap.get(gridItem[fieldToSearch])
          : null;

        return !!item ? {item, index} : null;
      })
      .filter(Boolean);

    for (const {item, index} of updatedItems) {
      data.splice(index, 1, item);
    }

    updatedItemsMap.clear();
    this.gridDataSubject$.next(data);
    this.gridSelectorService.resetSelectedKeys();
    this.triggerUpdateGrid();
  }

  public removeRow(row: any, fieldToSearch: string) {
    const gridData = this.gridDataSubject$.getValue();
    const lastElementDeleted = gridData.length === 1;

    if (lastElementDeleted) {
      return this.refreshGrid();
    }

    this.gridDataSubject$.next(gridData.filter(item => item[fieldToSearch] !== row[fieldToSearch]));
    this.gridSelectorService.selectedKeys$.next([]);
    this.totalItemsSubject$.next(this.totalItemsSubject$.value - 1);

    const total = this.totalItemsSubject$.getValue();
    const {pageSize, pageIndex} = this.gridPagerService.getPageSizeAndIndex();
    const skipPage = pageSize * pageIndex;
    const page = skipPage / pageSize + 1;
    const itemsPerPage = pageSize;
    const lastElementOnPage = page * itemsPerPage - total;

    if (lastElementOnPage === itemsPerPage) {
      const pageEvent = {pageIndex: pageIndex - 1, pageSize};
      this.gridPagerService.handlePageChange(pageEvent as PageEvent);
    }

    this.triggerUpdateGrid();
  }

  private refreshGrid() {
    this.gridDataSubject$.next([]);
    this.gridSelectorService.selectedKeys$.next([]);
    this.triggerUpdateGrid();
  }

  private triggerUpdateGrid(): void {
    this.needUpdateGridSubject$.next(true);
  }

  private triggerUpdateSelection(item, fieldToSearch) {
    const selectedItems = this.gridSelectorService.selectedKeys$.getValue().map(gridItem => {
      if (gridItem[fieldToSearch] === item[fieldToSearch]) {
        return item;
      }

      return gridItem;
    });

    this.gridSelectorService.handleKeySelectionChange(selectedItems);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.filtersSubject$.next(filter);
  }

  public sortChange(sort: SortDescriptor): void {
    this.sortSubject$.next(sort);
  }

  public stopLoaders() {
    this.loading = false;
    this.upFetching = false;
  }
}
